// src/utils/analytics.js
// Firebase Analytics wrapper that only initializes with consent

import { getAnalytics, logEvent, isSupported } from 'firebase/analytics';
import { app } from '../firebase';
import { hasConsent, COOKIE_CATEGORIES } from './cookies';

let analytics = null;
let analyticsInitialized = false;

/**
 * Initialize Firebase Analytics (only if Analytics consent is given)
 * @returns {Promise<boolean>} - True if initialized, false if blocked
 */
export async function initializeAnalytics() {
  // Don't initialize if already done
  if (analyticsInitialized) {
    return analytics !== null;
  }
  
  // Check consent
  if (!hasConsent(COOKIE_CATEGORIES.ANALYTICS)) {
    console.log('Analytics blocked - no consent');
    analyticsInitialized = true;
    return false;
  }
  
  // Check if analytics is supported
  const supported = await isSupported();
  if (!supported) {
    console.log('Analytics not supported in this environment');
    analyticsInitialized = true;
    return false;
  }
  
  try {
    // Initialize with privacy-friendly settings
    analytics = getAnalytics(app, {
      // IP anonymization is enabled by default in Firebase Analytics
      // Google Signals and ad personalization should be disabled in Firebase Console
    });
    
    analyticsInitialized = true;
    console.log('Firebase Analytics initialized');
    return true;
  } catch (error) {
    console.error('Error initializing Firebase Analytics:', error);
    analyticsInitialized = true;
    return false;
  }
}

/**
 * Log an analytics event (only if analytics is initialized)
 * @param {string} eventName - Event name
 * @param {Object} eventParams - Event parameters
 */
export function logAnalyticsEvent(eventName, eventParams = {}) {
  if (!analytics) {
    return;
  }
  
  try {
    logEvent(analytics, eventName, eventParams);
  } catch (error) {
    console.error('Error logging analytics event:', error);
  }
}

/**
 * Disable analytics (when consent is withdrawn)
 */
export function disableAnalytics() {
  analytics = null;
  analyticsInitialized = true;
  console.log('Analytics disabled');
}

/**
 * Check if analytics is currently active
 * @returns {boolean}
 */
export function isAnalyticsActive() {
  return analytics !== null && analyticsInitialized;
}
